import React, { useEffect, useState } from 'react';
import { Form, Input, DatePicker, InputNumber, Select, Button, message, Spin, Alert } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { TournamentService } from '@/services/tournament.service';
import { CategoryService } from '@/services/category.service';
import type { UpdateTournamentDTO } from '@/types/api.types';
import type { Category } from '@/types/models/tournament';
import { logger } from '@/utils/logger';
import moment from 'moment';
import 'moment/locale/ru';
import { customLocale } from '@/utils/datePickerUtils';
import { TOURNAMENT_STATUS, TOURNAMENT_STATUS_LABELS } from '@/constants/tournament.constants';

moment.locale('ru');

const { TextArea } = Input;
const { Option } = Select;

const EditTournament: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [categories, setCategories] = useState<Category[]>([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [categoryError, setCategoryError] = useState<string | null>(null);
    const [tournamentCategories, setTournamentCategories] = useState<number[]>([]);

    // Загрузка категорий
    useEffect(() => {
        const fetchCategories = async () => {
            setLoadingCategories(true);
            setCategoryError(null);
            try {
                const data = await CategoryService.getAll();
                setCategories(data);
            } catch (error) {
                logger.error('Error loading categories:', error);
                setCategoryError('Не удалось загрузить категории');
            } finally {
                setLoadingCategories(false);
            }
        };

        fetchCategories();
    }, []);

    // Загрузка данных турнира
    useEffect(() => {
        const fetchTournament = async () => {
            if (!id) return;

            try {
                const tournament = await TournamentService.getById(Number(id));

                // Извлекаем ID категорий из турнира
                const categoryIds = tournament.tournamentCategories?.map(tc => tc.weightCategory.id) || [];
                setTournamentCategories(categoryIds);

                form.setFieldsValue({
                    ...tournament,
                    tournamentDate: moment(tournament.tournamentDate),
                    registrationDeadline: moment(tournament.registrationDeadline),
                    weighingDates: tournament.weighingDates?.map((date: string) => moment(date)),
                    categoryIds: categoryIds
                });
            } catch (error) {
                logger.error('Error fetching tournament:', error);
                message.error('Ошибка при загрузке турнира');
            } finally {
                setLoading(false);
            }
        };

        fetchTournament();
    }, [id, form]);

    const onFinish = async (values: any) => {
        if (!id) return;

        setSubmitting(true);
        try {
            const formattedValues: UpdateTournamentDTO = {
                ...values,
                tournamentDate: values.tournamentDate.format('YYYY-MM-DD'),
                registrationDeadline: values.registrationDeadline.format('YYYY-MM-DD'),
                weighingDates: values.weighingDates?.map((date: moment.Moment) =>
                    date.format('YYYY-MM-DD')),
                categoryIds: values.categoryIds || [],
                status: values.status // Явно указываем статус
            };


            await TournamentService.update(Number(id), formattedValues);

            // Обновляем категории турнира, если они изменились
            if (values.categoryIds && JSON.stringify(values.categoryIds) !== JSON.stringify(tournamentCategories)) {
                try {
                    const categories = values.categoryIds.map((categoryId: number) => ({
                        tournamentId: Number(id),
                        categoryId,
                        status: 'active' as const
                    }));

                    await TournamentService.updateCategories(Number(id), categories);
                    
                } catch (categoryError) {
                    logger.error('Error updating tournament categories:', categoryError);
                    message.warning('Турнир обновлен, но возникла ошибка при обновлении категорий');
                }
            }

            message.success('Турнир успешно обновлен');
            navigate(`/tournaments/${id}`);
        } catch (error: any) {
            logger.error('Error updating tournament:', error);
            message.error(error.response?.data?.message || 'Ошибка при обновлении турнира');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <Spin size="large" />;
    }

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
        >
            <Form.Item
                name="name"
                label="Название турнира"
                rules={[{ required: true, message: 'Введите название турнира' }]}
            >
                <Input />
            </Form.Item>

            <Form.Item
                name="location"
                label="Место проведения"
                rules={[{ required: true, message: 'Укажите место проведения' }]}
            >
                <Input />
            </Form.Item>

            <Form.Item
                name="tournamentDate"
                label="Дата проведения"
                rules={[{ required: true, message: 'Выберите дату проведения' }]}
            >
                <DatePicker 
                    format="DD.MM.YYYY"
                    locale={customLocale}
                    placeholder="Выберите дату (ДД.ММ.ГГГГ)"
                    picker="date"
                    allowClear
                    showToday
                    disabledDate={() => false}
                    style={{ width: '100%' }}
                />
            </Form.Item>

            <Form.Item
                name="registrationDeadline"
                label="Окончание регистрации"
                rules={[{ required: true, message: 'Выберите окончание регистрации' }]}
            >
                <DatePicker 
                    format="DD.MM.YYYY"
                    locale={customLocale}
                    placeholder="Выберите дату (ДД.ММ.ГГГГ)"
                    picker="date"
                    allowClear
                    showToday
                    disabledDate={() => false}
                    style={{ width: '100%' }}
                />
            </Form.Item>

            <Form.Item
                name="feeForClubMembers"
                label="Взнос для членов клуба"
                rules={[{ required: true, message: 'Укажите взнос для членов клуба' }]}
            >
                <InputNumber min={0} />
            </Form.Item>

            <Form.Item
                name="feeForOthers"
                label="Взнос для остальных участников"
                rules={[{ required: true, message: 'Укажите взнос для остальных участников' }]}
            >
                <InputNumber min={0} />
            </Form.Item>

            <Form.Item
                name="weighingDates"
                label="Даты взвешивания"
            >
                <DatePicker.RangePicker 
                    format="DD.MM.YYYY"
                    locale={customLocale}
                    placeholder={['Начало', 'Конец']}
                    style={{ width: '100%' }}
                    disabledDate={() => false}
                />
            </Form.Item>

            <Form.Item
                name="description"
                label="Описание"
            >
                <TextArea rows={4} />
            </Form.Item>

            <Form.Item
                name="status"
                label="Статус"
                rules={[{ required: true, message: 'Выберите статус турнира' }]}
            >
                <Select>
                    {Object.entries(TOURNAMENT_STATUS)
                        .filter(([key]) => key !== 'DELETED')
                        .map(([_, value]) => (
                            <Option key={value} value={value}>
                                {TOURNAMENT_STATUS_LABELS[value]}
                            </Option>
                        ))}
                </Select>
            </Form.Item>

            <Form.Item
                name="categoryIds"
                label="Категории"
                help="Выберите категории для турнира"
            >
                {categoryError && <Alert message={categoryError} type="error" style={{ marginBottom: 16 }} />}
                <Select
                    mode="multiple"
                    placeholder="Выберите категории"
                    loading={loadingCategories}
                    style={{ width: '100%' }}
                    optionFilterProp="children"
                    showSearch
                >
                    {categories.map(category => (
                        <Option key={category.id} value={category.id}>
                            {category.name} ({category.ageFrom}-{category.ageTo} лет, {category.weightFrom}-{category.weightTo} кг, {category.gender})
                        </Option>
                    ))}
                </Select>
            </Form.Item>

            <Form.Item>
                <Button type="primary" htmlType="submit" loading={submitting}>
                    Сохранить изменения
                </Button>
            </Form.Item>
        </Form>
    );
};

export default EditTournament;